import React from "react";
import _ from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";
import { Button } from "reactstrap";
import { Link } from "react-router-dom";
import { Map } from "immutable";

import { Icon } from "@ui/Icon";
import { CellDateTime, CellViewLink } from "@ui/Table/Cells";
import { Table } from "@ui/Table";
import { compose } from "@util/compose";
import { withLocale, InjectedLocaleProps } from "@localisation/components/withLocale";
import { BundleStatus as BundleStatusComponent } from "../BundleStatus";
import { Bundle, BundleStatus } from "../../models";
import { messages } from "../../messages";

interface BundlesTableProps {
    bundles: Bundle[];
}

interface Row {
    entity: Bundle;
    id: number;
}

// Type the table component
type TypedTable = new () => Table<Row>;
const TypedTable = Table as TypedTable;

type Props = BundlesTableProps & InjectedLocaleProps;

const ViewUpdateButton = ({ value }: { value: string }) => {
    const buttonProps = {
        to: value,
        tag: Link,
        key: "update",
        outline: true,
    };
    return (
        <Button {...buttonProps}>
            <Icon name="pencil" />
            {" "}
            <FormattedMessage {...messages.viewUpdate} />
        </Button>
    );
};

const BundlesTable = ({ bundles, locale }: Props) => {
    const intl = useIntl();
    const CellBundleStatus = ({ value }: { value: Map<string, any>}) => {
        const status = value.toJS() as BundleStatus;
        return <BundleStatusComponent locale={locale} value={status} />;
    };
    const data = _.map(bundles, (bundle) => {
        return {entity: bundle, id: bundle.id} as Row;
    });
    return (
        <TypedTable data={data} pageSize={data.length} layout="basic">
            <RowDefinition>
                <ColumnDefinition
                    key="title"
                    id="entity.attrs.title"
                    title=" "
                    sortable={false}
                />
                <ColumnDefinition
                    key="album"
                    id="entity.album"
                    title={intl.formatMessage(messages.bundleColumnAlbum)}
                    sortable={false}
                    customComponent={CellViewLink}
                />
                <ColumnDefinition
                    key="status"
                    id="entity.status"
                    title={intl.formatMessage(messages.bundleColumnStatus)}
                    customComponent={CellBundleStatus}
                    sortable={false}
                />
                <ColumnDefinition
                    key="user"
                    id="entity.user.name"
                    title={intl.formatMessage(messages.bundleColumnUser)}
                    sortable={false}
                />
                <ColumnDefinition
                    key="createdAt"
                    id="entity.createdAt"
                    title={intl.formatMessage(messages.bundleColumnCreatedAt)}
                    customComponent={CellDateTime}
                    sortable={false}
                />
                <ColumnDefinition
                    key="updatedAt"
                    id="entity.updatedAt"
                    title={intl.formatMessage(messages.bundleColumnUpdatedAt)}
                    customComponent={CellDateTime}
                    sortable={false}
                />
                <ColumnDefinition
                    key="view"
                    id="entity.viewUrl"
                    title=" "
                    customComponent={ViewUpdateButton}
                    sortable={false}
                />
            </RowDefinition>
        </TypedTable>
    );
};

const ConnectedBundlesTable = compose<Props, BundlesTableProps>(
    withLocale,
)(BundlesTable);

export {
    BundlesTable as BundlesTablePresenter,
    ConnectedBundlesTable as BundlesTable,
    Props as BundlesTableProps,
    Row as BundlesTableRow,
    ViewUpdateButton,
};
